import Icon, { DeleteOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Divider, Dropdown, message } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { taskCompletedProperties } from '../../analytics/analyticsHelpers';
import {
  addTaskComment,
  addTaskLink,
  createPlaceholderTask,
  deleteTaskAttachment,
  deleteTaskAttachments,
  deleteTaskComment,
  deleteTaskLink,
  deleteTaskLinks,
  updateTaskComment,
  updateTaskLink,
  uploadTaskAttachment,
  uploadTaskDescriptionAttachment,
  uploadTemporaryAttachment,
} from '../../api/task.api';
import { deleteTasks } from '../../api/workspace';
import { AIIcon } from '../../assets/CustomIcons/AIIcon';
import { ArchiveBox } from '../../assets/CustomIcons/ArchiveBox';
import { BacklogCircle } from '../../assets/CustomIcons/BacklogCircle';
import { ExpandDown } from '../../assets/CustomIcons/ExpandDown';
import { TriageTriangle } from '../../assets/CustomIcons/TriageTriangle';
import AttachmentSvg from '../../assets/icons/attachment.svg?react';
import LinkSvg from '../../assets/icons/link.svg?react';
import SubitemSvg from '../../assets/icons/subitem.svg?react';
import { getWebsiteDomain } from '../../authConfig';
import { useGetUserFromAccessToken } from '../../common/auth';
import { AITaskBreakdownModal } from '../../components/Modal/AITaskBreakdownModal';
import { AITaskDescriptionModal } from '../../components/Modal/AITaskDescriptionModal';
import { DeleteTaskAttachmentModal } from '../../components/Modal/DeleteTaskAttachmentModal';
import { DeleteTaskLinkModal } from '../../components/Modal/DeleteTaskLinkModal';
import { DeleteTaskModal } from '../../components/Modal/DeleteTaskModal';
import { DeleteTasksModal } from '../../components/Modal/DeleteTasksModal';
import { TaskLinkModal } from '../../components/Modal/TaskLinkModal';
import { buildAddSubitemModalProps } from '../../components/Modal/createItemSpace';
import { PriorityOptions } from '../../components/TaskTable/helpers/helper';
import { BaseButton } from '../../components/UI/BaseButton';
import EpicDropdown from '../../components/UI/Dropdown/EpicDropdown';
import EstimateDropdown from '../../components/UI/Dropdown/EstimateDropdown';
import LabelDropdown from '../../components/UI/Dropdown/LabelDropdown';
import OwnerDropdown from '../../components/UI/Dropdown/OwnerDropdown';
import ParentTaskDropdown from '../../components/UI/Dropdown/ParentTaskDropdown';
import PriorityDropdown from '../../components/UI/Dropdown/PriorityDropdown';
import SprintStatusDropdown from '../../components/UI/Dropdown/SprintStatusDropdown';
import TaskStatusDropdown from '../../components/UI/Dropdown/TaskStatusDropdown';
import TaskTypeDropdown from '../../components/UI/Dropdown/TaskTypeDropdown';
import { SprintStatusType } from '../../components/UI/SprintStatusLabel';
import CustomTooltip from '../../components/UI/Tooltip/CustomTooltip';
import { selectVisibleSubitems } from '../../utils/subitems';
import { sprintStatusToTaskUpdates } from '../../utils/taskListUtils';
import { FILE_SIZE_LIMIT } from '../../constants/fileSize';
import { ModalContext, ModalType } from '../../context/ModalContext';
import { useAITaskSuggestion } from '../../hooks/useAITaskSuggestion';
import { useBatchActions } from '../../hooks/useBatchActions';
import { useTaskDetails } from '../../hooks/useTaskDetails';
import { useNavigateToTask, useTaskNavigation } from '../../hooks/useTaskNavigation';
import { queryClient } from '../../queryClient';
import { optimisticUpdateListItem } from '../../serverState/helpers/optimisticCache';
import { getCurrentWorkspaceActiveSprint } from '../../serverState/sprint';
import {
  useGetTaskAttachments,
  useGetTaskComments,
  useGetTaskLinks,
  useGetTaskSubtasks,
  useGetTaskDependencies,
  useAddTaskDependency,
  useRemoveTaskDependency,
  useUpdateTaskMutation,
  useUpdateTaskLabelsMutation,
  writeTaskDetailCache,
  removeTaskDetailCache,
  rollbackTaskDetailCache,
  mergeTaskListRow,
} from '../../serverState/tasks';
import { queryKeys } from '../../serverState/queryKeys';
import { useGetWorkspace } from '../../serverState/workspace';
import { SubtaskSuggestion } from '../../types/AI';
import { CommentAttachment } from '../../types/CommentAttachment';
import { Epic } from '../../types/Epic';
import Task from '../../types/Task';
import { reconcileStageChange, taskListCacheKey } from '../../serverState/helpers/taskStageCache';
import { FLOATING_ACTION_BAR_SPACE } from '../../constants/floatingActionBar';
import { TaskAttachment } from '../../types/TaskAttachment';
import { TaskLink } from '../../types/TaskLink';
import { TaskStatusGroup } from '../../types/TaskStatus';
import { TaskType } from '../../types/TaskType';
import { WorkspaceEstimationType } from '../../types/Workspace';
import { WorkspaceView } from '../../types/WorkspaceView';
import { extractApiErrorMessage } from '../../utils/errorHandling';
import { TaskComments, TaskDependencies, TaskDescription, TaskHeader, TaskLinks, TaskPullRequests } from './components';
import { useGetTaskPullRequests } from '../../serverState/github';
import { TaskDependencyTask } from '../../types/TaskDependency';
import { ActivityLog } from './components/activity/ActivityLog';
import { TaskAttachments } from './components/TaskAttachments';
import { TaskSubtasks } from './components/TaskSubtasks';
import { DeadlineEditableField } from './field/DeadlineEditableField';
import { EditableTaskFieldType } from './field/EditableTaskFields';
import { TitleEditableField } from './field/TitleEditableField';

interface TaskViewProps {
  view?: WorkspaceView;
}

export const TaskView: React.FC<TaskViewProps> = ({ view }) => {
  const { taskId, orgId, spaceId } = useParams<{ taskId: string; orgId: string; spaceId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const navigateToTask = useNavigateToTask();
  const [messageApi, contextHolder] = message.useMessage();
  const activityLogEnabled = import.meta.env.VITE_FEATURE_ACTIVITY_LOG === 'true';
  const [activeBottomTab, setActiveBottomTab] = useState<'comments' | 'activity'>('comments');
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [descriptionValue, setDescriptionValue] = useState<string | undefined>(undefined);
  const [isDescriptionSaving, setIsDescriptionSaving] = useState(false);
  const [savingIndicatorTimeout, setSavingIndicatorTimeout] = useState<ReturnType<typeof setTimeout>>();
  const [openTaskLinkModal, setOpenTaskLinkModal] = useState<boolean>(false);
  const [taskLinkToEdit, setTaskLinkToEdit] = useState<TaskLink | null>(null);
  const [openDeleteTaskLinkModal, setOpenDeleteTaskLinkModal] = useState<boolean>(false);
  const [taskLinksToDelete, setTaskLinksToDelete] = useState<TaskLink[]>([]);
  const [openDeleteTaskAttachmentModal, setOpenDeleteTaskAttachmentModal] = useState<boolean>(false);
  const [taskAttachmentsToDelete, setTaskAttachmentsToDelete] = useState<TaskAttachment[]>([]);
  const [openDeleteTasksModal, setOpenDeleteTasksModal] = useState<boolean>(false);
  const [openAIBreakdownModal, setOpenAIBreakdownModal] = useState<boolean>(false);
  const [openAIDescriptionModal, setOpenAIDescriptionModal] = useState<boolean>(false);
  const {
    task,
    setTask,
    epics,
    parentTaskOptions,
    usersWithAccessToSpace,
    taskStatuses,
    sprintStatuses,
    sprintStatus,
    framework,
    activeSprintId,
    planningSessionId,
  } = useTaskDetails(Number(taskId));

  const { previousTaskId, nextTaskId } = useTaskNavigation(view, task?.parentTaskId);
  const currentUserId = useGetUserFromAccessToken().data?.user.id;
  const { data: workspace } = useGetWorkspace();
  const activeSprintQuery = getCurrentWorkspaceActiveSprint();
  const estimationType = workspace?.estimationType ?? WorkspaceEstimationType.storyPoints;
  const taskCommentsQuery = useGetTaskComments(task?.id);
  const taskLinksQuery = useGetTaskLinks(task?.id);
  const taskAttachmentsQuery = useGetTaskAttachments(task?.id);
  const taskSubtasksQuery = useGetTaskSubtasks(task?.id);
  const dependenciesQuery = useGetTaskDependencies(task?.id);
  const taskPullRequestsQuery = useGetTaskPullRequests(task?.id);
  // workspaceId (number), NOT the route spaceId string — see useAddTaskDependency.
  const addDependency = useAddTaskDependency(task?.id ?? 0, task?.workspaceId ?? 0);
  const removeDependency = useRemoveTaskDependency(task?.id ?? 0, task?.workspaceId ?? 0);
  // Keyed mutation, not a bare updateTask: the socket layer defers incoming task
  // events while a write for that task is pending (helpers/inFlightTasks.ts), and a
  // bare API call is invisible to it — a late echo would revert the edit on screen.
  const updateTaskMutation = useUpdateTaskMutation();
  const updateTaskLabelsMutation = useUpdateTaskLabelsMutation();

  const disabledFields: EditableTaskFieldType[] = [];

  const [tasksHistory, setTasksHistory] = useState<Task[]>([]);
  const { openModal } = useContext(ModalContext);

  useEffect(() => {
    setDescriptionValue(task?.description);
  }, [task?.description]);

  // Track screen opened event for task view
  useEffect(() => {
    if (taskId) {
      analytics.track(AnalyticsEvent.screenOpened, { screen: 'task' });
    }
  }, [taskId]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (descriptionValue === task?.description) return;

      if (savingIndicatorTimeout) {
        clearTimeout(savingIndicatorTimeout);
        setSavingIndicatorTimeout(undefined);
      }

      setSavingIndicatorTimeout(
        setTimeout(() => {
          setIsDescriptionSaving(true);
          setTimeout(() => {
            setIsDescriptionSaving(false);
            setSavingIndicatorTimeout(undefined);
          }, 2000);
        }, 1000)
      );

      saveTask({ ...task, description: descriptionValue });
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [descriptionValue]);

  const saveTask = async (updatedTask: Task) => {
    if (!task) return;

    if (
      task.statusId !== updatedTask.statusId &&
      taskStatuses.find((status) => status.id === updatedTask.statusId)?.group === TaskStatusGroup.completed
    ) {
      // The Blocking / Blocked By panel reads dependenciesQuery, so that is the truth when loaded;
      // otherwise the detail record's own edges are used.
      analytics.track(
        AnalyticsEvent.taskCompleted,
        taskCompletedProperties(updatedTask, workspace, dependenciesQuery.data)
      );
    }
    const oldParentId = task?.parentTaskId;
    const oldEpicId = task?.epicId;

    // Optimistically bump the "Last edited by" / "Last edited" metadata so these
    // fields sync in realtime on every edit. The backend recomputes them
    // authoritatively and broadcasts the result via the `taskUpdated` socket
    // event (which full-spreads the task), so this keeps the editor's own view
    // fresh without waiting for a refetch.
    const optimisticTask: Task = {
      ...updatedTask,
      lastEditedBy: currentUserId ?? updatedTask.lastEditedBy,
      updatedAt: new Date(),
    };

    // Snapshot local + cache state so we can roll back if the save fails.
    const previousTask = task;
    const cacheKey = taskListCacheKey(task);

    // Task View now renders the detail record, not the list row, so patching the list
    // alone leaves the open view stale. `spaceId` comes from useParams() and is
    // `string | undefined`; String(undefined) is 'undefined', which would miss the
    // hook's '' fallback key.
    const detailKey = queryKeys.taskDetail(spaceId ?? '', optimisticTask.taskId);
    const previousDetail = queryClient.getQueryData<Task>(detailKey);

    setTask(optimisticTask);
    writeTaskDetailCache(optimisticTask);
    const rollbackCache = optimisticUpdateListItem<Task>(queryClient, cacheKey, optimisticTask, mergeTaskListRow);

    try {
      await updateTaskMutation.mutateAsync({ taskId: Number(updatedTask.id), task: updatedTask });
    } catch (error) {
      // Restore the editor and the list cache, then surface the failure instead
      // of leaving the optimistic value on screen.
      setTask(previousTask);
      rollbackCache();
      rollbackTaskDetailCache(detailKey, previousDetail);
      void messageApi.error(extractApiErrorMessage(error, 'Failed to save changes'));
      return;
    }

    // A stage change that crosses the archive boundary must move the row between
    // the default and archive task caches, otherwise the Archive view keeps a
    // stale copy (DEV-2528). Epics aren't archived, so skip them.
    if (task.type !== TaskType.epic && previousTask.stage !== updatedTask.stage) {
      reconcileStageChange(task.workspaceId, updatedTask.id, previousTask.stage, { stage: updatedTask.stage });
    }

    // Invalidate taskSubtasks query (only after a successful save)
    if (oldParentId && oldParentId !== updatedTask.parentTaskId) {
      await queryClient.invalidateQueries({ queryKey: ['taskSubtasks', oldParentId] });
    }
    if (oldEpicId && oldEpicId !== updatedTask.epicId) {
      await queryClient.invalidateQueries({ queryKey: ['taskSubtasks', oldEpicId] });
    }
    if (updatedTask.parentTaskId) {
      await queryClient.invalidateQueries({ queryKey: ['taskSubtasks', updatedTask.parentTaskId] });
    }
    if (updatedTask.epicId) {
      await queryClient.invalidateQueries({ queryKey: ['taskSubtasks', updatedTask.epicId] });
    }
  };

  // Labels go through their own endpoint, so they can't reuse saveTask — but they get the
  // same treatment: keyed mutation, optimistic local + cache write, rollback on failure.
  const saveTaskLabels = async (labelIds: number[]) => {
    if (!task) return;

    const previousTask = task;
    const optimisticTask: Task = {
      ...task,
      TaskLabels: labelIds.map((labelId) => ({ labelId, taskId: task.id })),
    };

    const detailKey = queryKeys.taskDetail(spaceId ?? '', task.taskId);
    const previousDetail = queryClient.getQueryData<Task>(detailKey);

    setTask(optimisticTask);
    writeTaskDetailCache(optimisticTask);
    const rollbackCache = optimisticUpdateListItem<Task>(
      queryClient,
      taskListCacheKey(task),
      optimisticTask,
      mergeTaskListRow
    );

    try {
      await updateTaskLabelsMutation.mutateAsync({ taskId: Number(task.id), labelIds });
    } catch (error) {
      setTask(previousTask);
      rollbackCache();
      rollbackTaskDetailCache(detailKey, previousDetail);
      void messageApi.error(extractApiErrorMessage(error, 'Failed to save labels'));
    }
  };

  const handleDelete = async (deleteSubtasks: boolean) => {
    try {
      if (!task) return;
      const tasksToDelete: number[] = [
        task.id,
        ...(deleteSubtasks ? (taskSubtasksQuery.data ?? []).map((subtask) => subtask.id) : []),
      ];
      await deleteTasks(task.workspaceId, tasksToDelete);

      setOpenDeleteModal(false);

      // Update caches synchronously before navigating
      queryClient.setQueryData(['epics', task.workspaceId], (oldDataSource: Epic[]) => {
        if (!oldDataSource) return oldDataSource;
        return oldDataSource.filter((item) => !tasksToDelete.includes(item.id));
      });
      queryClient.setQueryData(['tasks', task.workspaceId], (oldDataSource: Task[]) => {
        if (!oldDataSource) return oldDataSource;
        return oldDataSource.filter((item) => !tasksToDelete.includes(item.id));
      });
      // Archived tasks live in the separate archive cache (DEV-2528).
      queryClient.setQueryData(['tasks', task.workspaceId, 'archive'], (oldDataSource: Task[]) => {
        if (!oldDataSource) return oldDataSource;
        return oldDataSource.filter((item) => !tasksToDelete.includes(item.id));
      });
      if (task.sprintId) {
        await activeSprintQuery.refetch();
      }

      window.history.back();

      // The detail cache is keyed by task NUMBER, so a deleted task's record survives a
      // list filter and would be served to the next open of that URL. (#3010)
      // Evicted AFTER the navigation: this view's observer is still mounted until then,
      // and an eviction under it makes it refetch a task the server has just deleted —
      // a guaranteed 404 (the awaited sprint refetch above leaves plenty of time for it).
      removeTaskDetailCache(tasksToDelete);
    } catch (e) {
      console.error(e);
    }
  };

  const createSubtask = () => {
    openModal(ModalType.CreateItem, buildAddSubitemModalProps(task));
  };

  const createLink = async (url: string, name: string) => {
    await addTaskLink(task.id, url, name);
    await queryClient.invalidateQueries({ queryKey: ['taskLinks', task.id] });
    setOpenTaskLinkModal(false);
    setTaskLinkToEdit(null);
  };

  const updateLink = async (taskLinkId: number, url: string, name: string) => {
    await updateTaskLink(task.id, taskLinkId, url, name);
    await queryClient.invalidateQueries({ queryKey: ['taskLinks', task.id] });
    setOpenTaskLinkModal(false);
    setTaskLinkToEdit(null);
  };

  const handleEditLink = (link: TaskLink) => {
    setTaskLinkToEdit(link);

    setTimeout(() => {
      setOpenTaskLinkModal(true);
    }, 100);
  };

  const handleDeleteLinks = async (links: TaskLink[]) => {
    if (links.length === 0) return;
    if (links.length > 1) {
      await deleteTaskLinks(task.id);
    } else {
      await deleteTaskLink(task.id, links[0].id);
    }
    await queryClient.invalidateQueries({ queryKey: ['taskLinks', task.id] });
  };

  const handleAddAttachment = () => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files) {
        const fileArray = Array.from(files);
        await handleFileUpload(fileArray);
      }
    };
    fileInput.click();
  };

  const handleFileUpload = async (files: File[]): Promise<TaskAttachment[]> => {
    // Check file count limit first
    if (files.length > 10) {
      await messageApi.error('You can upload up to 10 files per batch.');
      return [];
    }

    const results: TaskAttachment[] = [];
    const oversizedFiles: string[] = [];
    const failedFiles: string[] = [];
    const successfulFiles: string[] = [];

    // Check for oversized files
    const validFiles = files.filter((file) => {
      if (file.size > FILE_SIZE_LIMIT) {
        oversizedFiles.push(file.name);
        return false;
      }
      return true;
    });

    // Upload valid files
    for (const file of validFiles) {
      try {
        const attachment = await uploadTaskAttachment(task.id, file);
        results.push(attachment.data);
        successfulFiles.push(file.name);
      } catch (error) {
        failedFiles.push(file.name);
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    // Invalidate queries if any files were uploaded successfully
    if (results.length > 0) {
      await queryClient.invalidateQueries({ queryKey: ['taskAttachments', task.id] });
    }

    // Handle different error scenarios
    if (oversizedFiles.length > 0 && failedFiles.length > 0) {
      // Both oversized and failed files
      await messageApi.error(
        `${oversizedFiles.length} file(s) exceed 100MB limit and ${failedFiles.length} file(s) failed to upload. ${successfulFiles.length} file(s) uploaded successfully.`
      );
    } else if (oversizedFiles.length > 0) {
      // Only oversized files
      if (successfulFiles.length > 0) {
        await messageApi.warning('Attachment(s) smaller than 100MB added.');
      } else {
        await messageApi.error('Attachment size must be less than 100MB');
      }
    } else if (failedFiles.length > 0) {
      // Only failed files
      if (successfulFiles.length > 0) {
        await messageApi.warning(
          `${failedFiles.length} file(s) failed to upload. ${successfulFiles.length} file(s) uploaded successfully.`
        );
      } else {
        await messageApi.error('All files failed to upload');
      }
    } else if (successfulFiles.length > 0) {
      await messageApi.success(
        successfulFiles.length > 1
          ? `${successfulFiles.length} files uploaded successfully.`
          : 'File uploaded successfully.'
      );
    }

    return results;
  };

  const handleDescriptionAttachmentUpload = async (file: File) => {
    if (file.size > FILE_SIZE_LIMIT) {
      await messageApi.error('Attachment size must be less than 100MB');
      return null;
    }
    try {
      const attachment = await uploadTaskDescriptionAttachment(task.id, file);
      return attachment.data;
    } catch (error) {
      await messageApi.error('Failed to upload attachment');
      console.error(error);
      return null;
    }
  };

  const handleCommentAttachmentUpload = async (files: File[]): Promise<CommentAttachment[]> => {
    // Check file count limit first
    if (files.length > 10) {
      await messageApi.error('You can upload up to 10 files per comment.');
      return [];
    }

    const results: CommentAttachment[] = [];
    const oversizedFiles: string[] = [];
    const failedFiles: string[] = [];
    const successfulFiles: string[] = [];

    // Check for oversized files
    const validFiles = files.filter((file) => {
      if (file.size > FILE_SIZE_LIMIT) {
        oversizedFiles.push(file.name);
        return false;
      }
      return true;
    });

    // Upload valid files
    for (const file of validFiles) {
      try {
        const attachment = await uploadTemporaryAttachment(task.id, file);
        results.push(attachment.data);
        successfulFiles.push(file.name);
      } catch (error) {
        failedFiles.push(file.name);
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    // Handle different error scenarios
    if (oversizedFiles.length > 0 && failedFiles.length > 0) {
      await messageApi.error(
        `${oversizedFiles.length} file(s) exceed 100MB limit and ${failedFiles.length} file(s) failed to upload. ${successfulFiles.length} file(s) uploaded successfully.`
      );
    } else if (oversizedFiles.length > 0) {
      if (successfulFiles.length > 0) {
        await messageApi.warning('Attachment(s) smaller than 100MB added.');
      } else {
        await messageApi.error('Attachment size must be less than 100MB');
      }
    } else if (failedFiles.length > 0) {
      if (successfulFiles.length > 0) {
        await messageApi.warning(
          `${failedFiles.length} file(s) failed to upload. ${successfulFiles.length} file(s) uploaded successfully.`
        );
      } else {
        await messageApi.error('All files failed to upload');
      }
    } else if (successfulFiles.length > 0) {
      await messageApi.success(
        successfulFiles.length > 1
          ? `${successfulFiles.length} files uploaded successfully.`
          : 'File uploaded successfully.'
      );
    }

    return results;
  };

  const handleDeleteAttachments = async (attachments: TaskAttachment[]) => {
    if (attachments.length === 0) return;
    if (attachments.length > 1) {
      await deleteTaskAttachments(task.id);
    } else {
      await deleteTaskAttachment(task.id, attachments[0].id);
    }
    await queryClient.invalidateQueries({ queryKey: ['taskAttachments', task.id] });
  };

  const onPreviousTask = () => {
    if (previousTaskId) {
      navigateToTask({ workspaceId: task.workspaceId, taskId: previousTaskId });
    }
  };

  const onNextTask = () => {
    if (nextTaskId) {
      navigateToTask({ workspaceId: task.workspaceId, taskId: nextTaskId });
    }
  };

  const onCopyTaskLink = async () => {
    await navigator.clipboard.writeText(`${getWebsiteDomain()}${location.pathname}`);
    await messageApi.success('Task link copied!');
  };

  const onCopyTaskDescriptionLink = async () => {
    await messageApi.success('Link copied!');
  };

  const extractMentionIds = (content: string): number[] => {
    const mentionIds: number[] = [];
    const mentionMatches = content.matchAll(/<span[^>]*data-type="mention"[^>]*data-id="(\d+)"[^>]*>/g);
    for (const match of mentionMatches) {
      mentionIds.push(parseInt(match[1], 10));
    }
    return mentionIds;
  };

  const onOpenSubtask = (subtask: Task) => {
    setTasksHistory([...tasksHistory, task]);
    navigateToTask(subtask);
  };

  const onOpenParentTask = (parentTask: Task) => {
    setTasksHistory([...tasksHistory, task]);
    navigateToTask(parentTask);
  };

  const { onDeleteTasks } = useBatchActions(
    task?.workspaceId.toString(),
    async (message, hidePanel) => {
      await queryClient.invalidateQueries({ queryKey: ['taskSubtasks', task.id] });
      await messageApi.success(message);
    },
    async (info) => {
      await messageApi.info(info);
    },
    async (error) => {
      await messageApi.error(error);
    }
  );

  const handleDeleteTasks = async () => {
    setOpenDeleteTasksModal(false);
    const subtaskIds = (taskSubtasksQuery.data ?? []).map((subtask) => subtask.id);
    onDeleteTasks(subtaskIds);
    // useBatchActions only patches the list caches; a deleted subitem's own detail
    // record would otherwise still be served if the user navigated to its URL. (#3010)
    // `onDeleteTasks` is fire-and-forget (no promise to await), so this evicts up
    // front — harmless if the delete fails, since an absent entry just refetches.
    removeTaskDetailCache(subtaskIds);
  };

  const handleGenerateAISuggestion = async () => {
    if (!task) return;

    // Open AI Description modal
    setOpenAIDescriptionModal(true);
  };

  const handleGenerateAIBreakdown = async () => {
    if (!task) return;

    setOpenAIBreakdownModal(true);
  };

  const handleAcceptAIBreakdown = async (selectedSubtasks: SubtaskSuggestion[]) => {
    if (!task) return;

    try {
      // Create subtasks in order
      for (const subtask of selectedSubtasks) {
        const response = await createPlaceholderTask(
          task.workspaceId,
          task.type === TaskType.epic ? task.id : (task.epicId ?? undefined),
          subtask.title,
          undefined,
          undefined, //taskStatuses[0]?.id,
          undefined, //subtask.priority,
          undefined,
          undefined, //subtask.estimatedHours && estimationType === WorkspaceEstimationType.hours ? subtask.estimatedHours : undefined,
          subtask.storyPoints && estimationType === WorkspaceEstimationType.storyPoints
            ? subtask.storyPoints
            : undefined,
          undefined,
          undefined,
          TaskType.task,
          task.type === TaskType.epic ? undefined : task.id,
          undefined,
          subtask.description
        );
      }

      setOpenAIBreakdownModal(false);
      // No writeTaskDetailCache here on purpose: creating subitems does not change the
      // parent's own record, and the new children have no detail entry yet, so these
      // list/subtask invalidations are the complete set. (#3010)
      await queryClient.invalidateQueries({ queryKey: ['tasks', task.workspaceId] });
      await queryClient.invalidateQueries({ queryKey: ['taskSubtasks', task.id] });
      await messageApi.success(
        task.type === TaskType.epic ? 'Item(s) have been added.' : 'Subitem(s) have been added.'
      );
    } catch (error) {
      console.error('Failed to create subtasks:', error);
      await messageApi.error('Failed to create subtasks');
    }
  };

  const handleCancelAIBreakdown = () => {
    setOpenAIBreakdownModal(false);
  };

  // Display is filtered through the shared predicate, but destructive operations below
  // (delete task + subitems, Delete All) intentionally read taskSubtasksQuery.data
  // directly and act on the complete, unfiltered set — leaving a hidden child behind
  // would orphan it, which is worse than a display/destructive-set mismatch. (#2884)
  const visibleSubtasks = task ? selectVisibleSubitems(taskSubtasksQuery.data ?? [], task) : [];

  const isSubitem = !!task?.parentTaskId;

  if (!task || !epics || !usersWithAccessToSpace || !taskStatuses) {
    return <div></div>;
  }

  return (
    <div className="relative h-full bg-brand-white">
      {contextHolder}
      <div className="flex h-full w-full">
        {/* Left Column - Main Task Info */}
        <div className="overflow-auto h-full flex-1">
          <TaskHeader
            onBack={() => {
              if (location.state?.from === 'decisions') {
                navigate(`/${orgId}/decisions`);
              } else if (location.state?.from === 'all-tasks') {
                navigate(`/${orgId}/all-tasks`);
              } else if (view && tasksHistory.length === 0) {
                navigate(`/${orgId}/space/${spaceId}/${view.group}?view=${view.publicId}`);
              } else {
                window.history.back();
                setTasksHistory(tasksHistory.slice(0, -1));
              }
            }}
            onPreviousTask={onPreviousTask}
            onNextTask={onNextTask}
            onCopyLink={onCopyTaskLink}
            onDelete={() => setOpenDeleteModal(true)}
            showBackButton={!!view}
            previousTaskId={previousTaskId}
            nextTaskId={nextTaskId}
            centerSlot={
              task.stage === 'triage' ? (
                <div className="inline-flex items-center gap-1 rounded bg-neutral-200 px-2 py-0.5 text-sm text-neutral-900">
                  <TriageTriangle />
                  <span>Task not triaged yet</span>
                </div>
              ) : task.stage === 'archive' ? (
                <div className="inline-flex items-center gap-1 rounded bg-neutral-200 px-2 py-0.5 text-sm text-neutral-900">
                  <ArchiveBox />
                  <span>Archived Task</span>
                </div>
              ) : null
            }
          />

          {/* On triage the quick-actions bar floats over the bottom of this column, so
              reserve the same space under the last element (the comment box) that the
              table views reserve under the last row — otherwise the bar covers it. #2357 */}
          <div
            className="flex flex-col px-10 pt-1 pb-6"
            style={task.stage === 'triage' ? { paddingBottom: FLOATING_ACTION_BAR_SPACE } : undefined}
          >
            <div>
              <TitleEditableField
                initialValue={task.title}
                onChange={async (title) => {
                  if (title) {
                    await saveTask({ ...task, title });
                  }
                }}
              />
            </div>

            <div className="flex justify-start gap-2">
              {task.stage !== 'triage' && (
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'manually',
                        label: <div className="text-neutral-900">Manually</div>,
                        icon: <EditOutlined className="text-neutral-900" />,
                        onClick: createSubtask,
                      },
                      {
                        key: 'vibe-breakdown',
                        label: (
                          <div className="flex items-center justify-between w-full text-neutral-900">
                            <div className="text-neutral-900">Vibe Breakdown</div>
                            <CustomTooltip title="AI will generate a list of proposed Items for this Epic">
                              <InfoCircleOutlined style={{ marginLeft: '24px' }} />
                            </CustomTooltip>
                          </div>
                        ),
                        icon: <AIIcon className="text-neutral-900" />,
                        onClick: handleGenerateAIBreakdown,
                      },
                    ],
                  }}
                  trigger={['click']}
                  disabled={isSubitem}
                >
                  <div>
                    <BaseButton
                      type="secondary"
                      size="medium"
                      disabled={isSubitem}
                      disabledTooltip={isSubitem ? 'Can not add Subitem to Task that is already Subitem' : ''}
                    >
                      <Icon component={SubitemSvg} />
                      {task?.type === TaskType.epic ? 'Add Item' : 'Add Subitem'}
                      <Icon component={ExpandDown} />
                    </BaseButton>
                  </div>
                </Dropdown>
              )}

              <BaseButton type="secondary" size="medium" onClick={() => setOpenTaskLinkModal(true)}>
                <Icon component={LinkSvg} />
                Add Link
              </BaseButton>

              <BaseButton type="secondary" size="medium" onClick={handleAddAttachment}>
                <Icon component={AttachmentSvg} />
                Add Attachment
              </BaseButton>
            </div>

            {!task.parentTaskId && (
              <TaskSubtasks
                task={task}
                subtasks={visibleSubtasks}
                setSubtasks={(next) => {
                  // Merge onto the cached list rather than replacing it: `next` is the
                  // filtered `visibleSubtasks`, so a plain overwrite would evict every
                  // child the filter hid (e.g. cross-space) until the next refetch. (#2884)
                  queryClient.setQueryData(['taskSubtasks', task.id], (prev: Task[] = []) => {
                    const edited = new Map(next.map((t) => [t.id, t]));
                    return prev.map((t) => edited.get(t.id) ?? t);
                  });
                  // An inline subitem edit happens with this Task View mounted, so the
                  // subitem's own detail record can't rely on a remount refetch to catch
                  // up — it would still serve the pre-edit row on the next open. (#3010)
                  next.forEach((subtask) => writeTaskDetailCache(subtask));
                }}
                usersWithAccessToSpace={usersWithAccessToSpace}
                onOpenSubtask={onOpenSubtask}
                onAddSubtask={createSubtask}
                onDeleteAllSubtasks={() => {
                  setOpenDeleteTasksModal(true);
                }}
                view={view}
              />
            )}

            <TaskLinks
              links={taskLinksQuery.data ?? []}
              onEditLink={handleEditLink}
              onDeleteLink={(link) => {
                setTaskLinksToDelete([link]);
                setOpenDeleteTaskLinkModal(true);
              }}
              onCreateLink={() => {
                setOpenTaskLinkModal(true);
              }}
              onDeleteAllLinks={() => {
                setTaskLinksToDelete(taskLinksQuery.data ?? []);
                setOpenDeleteTaskLinkModal(true);
              }}
            />

            {/* Between Links and Attachments: both are references out of the task, and a
                pull request is the one the GitHub integration produces on its own. The
                section renders nothing when the task has no links, so a task that has
                never seen a pull request is unchanged. */}
            <TaskPullRequests pullRequests={taskPullRequestsQuery.data ?? []} />

            <TaskAttachments
              attachments={taskAttachmentsQuery.data ?? []}
              onDeleteAttachment={(attachment) => {
                setTaskAttachmentsToDelete([attachment]);
                setOpenDeleteTaskAttachmentModal(true);
              }}
              onDeleteAllAttachments={() => {
                setTaskAttachmentsToDelete(taskAttachmentsQuery.data ?? []);
                setOpenDeleteTaskAttachmentModal(true);
              }}
              onAddAttachment={handleAddAttachment}
            />

            <div>
              <TaskDescription
                description={descriptionValue}
                disabledFields={disabledFields}
                onDescriptionChange={setDescriptionValue}
                isDescriptionSaving={isDescriptionSaving}
                onImageUpload={handleDescriptionAttachmentUpload}
                onCopyTaskDescriptionLink={onCopyTaskDescriptionLink}
                onGenerateAISuggestion={handleGenerateAISuggestion}
                usersWithAccessToSpace={usersWithAccessToSpace}
              />

              {activityLogEnabled && (
                <div className="mb-3 inline-flex w-fit items-center border-x-0 border-t-0 border-b border-solid border-neutral-300">
                  {(['comments', 'activity'] as const).map((tab) => (
                    <div
                      key={tab}
                      role="tab"
                      tabIndex={0}
                      aria-selected={activeBottomTab === tab}
                      onClick={() => setActiveBottomTab(tab)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') setActiveBottomTab(tab);
                      }}
                      className={`px-2 py-1.5 -mb-px text-sm font-medium leading-5 border-x-0 border-t-0 border-b-2 border-solid cursor-pointer transition-colors ${
                        activeBottomTab === tab
                          ? 'border-brand-taskonio-green text-brand-grey-dark'
                          : 'border-transparent text-neutral-900 hover:text-brand-grey-dark'
                      }`}
                    >
                      {tab === 'comments' ? 'Comments' : 'Activity Log'}
                    </div>
                  ))}
                </div>
              )}

              {activityLogEnabled && activeBottomTab === 'activity' && (
                <ActivityLog task={task} usersWithAccessToSpace={usersWithAccessToSpace} />
              )}

              <div hidden={activityLogEnabled && activeBottomTab !== 'comments'}>
                <TaskComments
                  hideHeading={activityLogEnabled}
                  comments={taskCommentsQuery.data ?? []}
                  isLoading={taskCommentsQuery.isLoading}
                  onCommentSubmit={async (commentValue: string, tempAttachmentKeys?: string[], parentId?: number) => {
                    if (commentValue) {
                      try {
                        const mentionIds = extractMentionIds(commentValue);
                        await addTaskComment(
                          task.id,
                          {
                            content: commentValue,
                            taskId: task.id,
                            mentionIds,
                            parentId,
                          },
                          tempAttachmentKeys
                        );
                        await queryClient.invalidateQueries({ queryKey: ['taskComments', task.id] });
                      } catch (error) {
                        console.error('Failed to post comment:', error);
                        void messageApi.error('Failed to post comment');
                        throw error;
                      }
                    }
                  }}
                  onEditComment={async (commentId: number, content: string) => {
                    try {
                      const mentionIds = extractMentionIds(content);
                      await updateTaskComment(task.id, commentId, content, mentionIds);
                      await queryClient.invalidateQueries({ queryKey: ['taskComments', task.id] });
                      // Use the context-aware messageApi (its contextHolder lives inside the
                      // dark-aware ConfigProvider) so the toast is themed like the task-created
                      // toast — the static `message.*` renders detached and stays light. #2227
                      messageApi.success('Comment updated successfully');
                    } catch (error) {
                      console.error('Failed to update comment:', error);
                      messageApi.error('Failed to update comment');
                    }
                  }}
                  onDeleteComment={async (commentId: number) => {
                    try {
                      await deleteTaskComment(task.id, commentId);
                      await queryClient.invalidateQueries({ queryKey: ['taskComments', task.id] });
                      messageApi.success('Comment deleted successfully');
                    } catch (error) {
                      console.error('Failed to delete comment:', error);
                      messageApi.error('Failed to delete comment');
                    }
                  }}
                  usersWithAccessToSpace={usersWithAccessToSpace}
                  onImageUpload={handleDescriptionAttachmentUpload}
                  onCopyTaskDescriptionLink={onCopyTaskDescriptionLink}
                  onCommentAttachmentUpload={handleCommentAttachmentUpload}
                  showWarningMessage={(warningMessage) => {
                    void messageApi.warning(warningMessage);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Task Details */}
        <div className="flex h-full w-96">
          <Divider type="vertical" className="h-full m-0" />
          <div className="h-full w-full px-6 py-6">
            <div>
              <div className="text-brand-grey-dark text-base font-semibold mb-3 cursor-default">Properties</div>

              {/* Item ID */}
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">ID</div>
                <div className="flex items-center px-2 cursor-default">
                  <div className="text-neutral-900 text-sm font-normal">DEV-{task.taskId}</div>
                </div>
              </div>

              {/* Item Type */}
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Type</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <TaskTypeDropdown
                    type={task.type as TaskType}
                    size="large"
                    onBlur={() => {}}
                    onSelect={async (type) => await saveTask({ ...task, type: type })}
                    disabled={task.type === TaskType.epic}
                  />
                </div>
              </div>

              {/* Epic */}
              {task.type !== TaskType.epic && (
                <div className="h-10 flex items-center justify-start gap-10">
                  <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Epic</div>
                  <div className="flex items-center px-2 cursor-pointer">
                    <EpicDropdown
                      epic={epics.find((epic) => epic.id === task.epicId) || null}
                      epics={epics}
                      onBlur={() => {}}
                      onSelect={async (epic) => {
                        await saveTask({ ...task, epicId: epic?.id || null });
                      }}
                      size="large"
                      disabled={!!task.parentTaskId}
                      onOpenEpic={onOpenParentTask}
                      showGoToIcon={true}
                    />
                  </div>
                </div>
              )}

              {/* Parent Task */}
              {task.type !== TaskType.epic && (
                <div className="h-10 flex items-center justify-start gap-10">
                  <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Parent Task</div>
                  <div className="flex items-center px-2 cursor-pointer">
                    <ParentTaskDropdown
                      task={parentTaskOptions.find((parentTask) => parentTask.id === task.parentTaskId) || null}
                      options={parentTaskOptions.filter((parentTask) => parentTask.id !== task.id)}
                      epics={epics}
                      onSelect={async (parentTask) => {
                        if (task.parentTaskId) {
                          await queryClient.invalidateQueries({ queryKey: ['taskSubtasks', task.parentTaskId] });
                        }
                        await saveTask({
                          ...task,
                          parentTaskId: parentTask?.id || null,
                          epicId: parentTask?.epicId ?? task.epicId,
                        });
                        if (parentTask?.id) {
                          await queryClient.invalidateQueries({ queryKey: ['taskSubtasks', parentTask.id] });
                        }
                      }}
                      disabled={taskSubtasksQuery.data ? taskSubtasksQuery.data.length > 0 : false}
                      onOpenParentTask={onOpenParentTask}
                    />
                  </div>
                </div>
              )}

              {/* Dependencies — Blocking / Blocked by */}
              {task.type !== TaskType.epic && (
                <TaskDependencies
                  task={task}
                  dependencies={dependenciesQuery.data ?? { blocking: [], blockedBy: [] }}
                  options={parentTaskOptions}
                  epics={epics}
                  onAdd={(relatedTaskId, type) => addDependency.mutate({ relatedTaskId, type })}
                  onRemove={(dependencyId, relatedTaskId) => removeDependency.mutate({ dependencyId, relatedTaskId })}
                  onOpenTask={(relatedTask: TaskDependencyTask) => {
                    setTasksHistory([...tasksHistory, task]);
                    navigateToTask({ workspaceId: task.workspaceId, taskId: relatedTask.taskId });
                  }}
                />
              )}

              {/* Sprint Status */}
              {task.type !== TaskType.epic && (
                <div className="h-10 flex items-center justify-start gap-10">
                  <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Stage</div>
                  <div className="flex items-center px-2 cursor-pointer">
                    <SprintStatusDropdown
                      sprintStatus={sprintStatus as SprintStatusType | null}
                      sprintStatuses={sprintStatuses}
                      onSelect={async (sprintStatus) => {
                        if (!sprintStatus) return;
                        await saveTask({ ...task, ...sprintStatusToTaskUpdates(sprintStatus, activeSprintId) });
                      }}
                      onBlur={() => {}}
                      size="large"
                    />
                  </div>
                </div>
              )}

              {/* Labels — kept right before Owner to match the table column order (#2224) */}
              {task.type !== TaskType.epic && (
                <div className="h-10 flex items-center justify-start gap-10">
                  <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Label</div>
                  <div className="flex items-center px-2 cursor-pointer">
                    <LabelDropdown
                      selectedLabels={
                        workspace?.Labels?.filter((label) => task.TaskLabels?.some((tl) => tl.labelId === label.id)) ||
                        []
                      }
                      availableLabels={workspace?.Labels || []}
                      onSelectionChange={async (labels) => {
                        await saveTaskLabels(labels.map((label) => label.id));
                      }}
                      onBlur={() => undefined}
                      size="large"
                      customAlign
                      emptyElement={<div className="text-neutral-900 text-sm font-normal">Add Label</div>}
                    />
                  </div>
                </div>
              )}

              {/* Owner */}
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Owner</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <OwnerDropdown
                    owner={usersWithAccessToSpace.find((user) => user.id === task.assignee) || null}
                    owners={usersWithAccessToSpace}
                    onSelect={async (user) => {
                      await saveTask({ ...task, assignee: user?.id || null });
                    }}
                    size="small"
                    emptyElement={<div className="text-neutral-900 text-sm font-normal">Add Owner</div>}
                  />
                </div>
              </div>

              {/* Status */}
              {task.type !== TaskType.epic && (
                <div className="h-10 flex items-center justify-start gap-10">
                  <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Status</div>
                  <div className="flex items-center px-2 cursor-pointer">
                    <TaskStatusDropdown
                      taskStatusId={task.statusId}
                      onSelect={(status) => {
                        void saveTask({ ...task, statusId: status.id });
                      }}
                      size="large"
                    />
                  </div>
                </div>
              )}

              {/* Priority */}
              {task.type !== TaskType.epic && (
                <div className="h-10 flex items-center justify-start gap-10">
                  <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Priority</div>
                  <div className="flex items-center px-2 cursor-pointer">
                    <PriorityDropdown
                      priority={task.priority || PriorityOptions.NoPriority}
                      size="large"
                      onSelect={async (priority) => {
                        await saveTask({ ...task, priority: priority });
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Estimate */}
              {task.type !== TaskType.epic && (
                <div className="h-10 flex items-center justify-start gap-10">
                  <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Estimate</div>
                  <div className="flex items-center px-2 cursor-pointer">
                    <EstimateDropdown
                      estimate={
                        (estimationType === WorkspaceEstimationType.storyPoints ? task.storyPoints : task.estimate) ||
                        null
                      }
                      size="large"
                      onSelect={async (estimate) => {
                        await saveTask({
                          ...task,
                          ...(estimationType === WorkspaceEstimationType.storyPoints
                            ? { storyPoints: estimate }
                            : { estimate: estimate }),
                        });
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Start Date */}
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Start Date</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <DeadlineEditableField
                    editingDisabled={false}
                    startDate={task.startDate || null}
                    targetDate={task.deadline || null}
                    mode="startDate"
                    onChange={async (startDate) => {
                      await saveTask({ ...task, startDate: startDate });
                    }}
                  />
                </div>
              </div>

              {/* Target Date */}
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Target Date</div>
                <div className="flex items-center px-2 cursor-pointer">
                  <DeadlineEditableField
                    editingDisabled={false}
                    startDate={task.startDate || null}
                    targetDate={task.deadline || null}
                    mode="targetDate"
                    onChange={async (deadline) => {
                      await saveTask({ ...task, deadline: deadline });
                    }}
                  />
                </div>
              </div>

              {/* Created at */}
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Created at</div>
                <div className="flex items-center px-2 cursor-default">
                  <div className="text-neutral-900 text-sm font-normal">
                    {dayjs(task.createdAt).format('MMM DD, YYYY, HH:mm')}
                  </div>
                </div>
              </div>

              {/* Created by */}
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Created by</div>
                <div className="flex items-center px-2">
                  <OwnerDropdown
                    owner={usersWithAccessToSpace.find((u) => u.id === task.createdBy) ?? null}
                    owners={[]}
                    size="small"
                    disabled={true}
                    emptyElement={<div className="text-neutral-900 text-sm font-normal">-</div>}
                  />
                </div>
              </div>

              {/* Last edited at */}
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Last edited at</div>
                <div className="flex items-center px-2 cursor-default">
                  <div className="text-neutral-900 text-sm font-normal">
                    {dayjs(task.updatedAt).format('MMM DD, YYYY, HH:mm')}
                  </div>
                </div>
              </div>

              {/* Last edited by */}
              <div className="h-10 flex items-center justify-start gap-10">
                <div className="w-28 shrink-0 text-neutral-800 text-sm font-medium cursor-default">Last edited by</div>
                <div className="flex items-center px-2">
                  <OwnerDropdown
                    owner={usersWithAccessToSpace.find((u) => u.id === (task.lastEditedBy ?? task.createdBy)) ?? null}
                    owners={[]}
                    size="small"
                    disabled={true}
                    emptyElement={<div className="text-neutral-900 text-sm font-normal">-</div>}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {task.stage === 'triage' && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[99] flex items-center">
          <div className="rounded-xl shadow bg-taskonioGreen-300 border-solid border-4 border-taskonioGreen-300 flex-col justify-start items-start gap-2.5 inline-flex">
            <div className="h-[66px] self-stretch px-6 bg-white rounded-xl border-solid border-2 border-taskonioGreen-600 justify-start items-center gap-6 inline-flex">
              <div className="flex-col justify-center items-center gap-0.5 inline-flex">
                <div className="text-brand-grey-dark text-[10px] font-medium leading-3">Triage</div>
                <div className="text-brand-grey-dark text-sm font-semibold leading-tight">Actions</div>
              </div>
              <div className="w-px h-[42px] bg-neutral-300 rounded-sm" />
              <div className="justify-start items-start gap-2 flex">
                <BaseButton
                  type="secondary"
                  size="medium"
                  onClick={async () => {
                    await saveTask({ ...task, stage: 'backlog', sprintId: null, planningSessionId: null });
                  }}
                >
                  <BacklogCircle />
                  Move to Backlog
                </BaseButton>
                <BaseButton
                  type="secondary"
                  size="medium"
                  onClick={async () => {
                    await saveTask({ ...task, stage: 'archive', sprintId: null, planningSessionId: null });
                  }}
                >
                  <ArchiveBox />
                  Move to Archive
                </BaseButton>
                <BaseButton type="text" size="medium" danger onClick={() => setOpenDeleteModal(true)}>
                  <DeleteOutlined />
                  Delete
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteTaskModal
        task={task}
        subtasks={taskSubtasksQuery.data ?? []}
        open={openDeleteModal}
        onDelete={handleDelete}
        onCancel={() => setOpenDeleteModal(false)}
      />

      {taskLinksToDelete.length > 0 && (
        <DeleteTaskLinkModal
          open={openDeleteTaskLinkModal}
          onOk={async () => {
            if (taskLinksToDelete.length > 0) {
              await handleDeleteLinks(taskLinksToDelete);
            }
            setOpenDeleteTaskLinkModal(false);
          }}
          onCancel={() => setOpenDeleteTaskLinkModal(false)}
          links={taskLinksToDelete}
        />
      )}

      {taskAttachmentsToDelete.length > 0 && (
        <DeleteTaskAttachmentModal
          open={openDeleteTaskAttachmentModal}
          onOk={async () => {
            await handleDeleteAttachments(taskAttachmentsToDelete);
            setOpenDeleteTaskAttachmentModal(false);
          }}
          onCancel={() => setOpenDeleteTaskAttachmentModal(false)}
          attachments={taskAttachmentsToDelete}
        />
      )}

      <TaskLinkModal
        open={openTaskLinkModal}
        onAdd={createLink}
        onEdit={updateLink}
        onCancel={() => {
          setOpenTaskLinkModal(false);
          setTaskLinkToEdit(null);
        }}
        confirmLoading={false}
        taskLink={taskLinkToEdit || undefined}
      />

      <DeleteTasksModal
        open={openDeleteTasksModal}
        onOk={handleDeleteTasks}
        onCancel={() => setOpenDeleteTasksModal(false)}
        tasks={taskSubtasksQuery.data ?? []}
        confirmLoading={false}
      />

      <AITaskBreakdownModal
        open={openAIBreakdownModal}
        task={task}
        onAccept={handleAcceptAIBreakdown}
        onCancel={handleCancelAIBreakdown}
        usersWithAccessToSpace={usersWithAccessToSpace}
      />

      <AITaskDescriptionModal
        task={task}
        open={openAIDescriptionModal}
        onClose={() => setOpenAIDescriptionModal(false)}
        usersWithAccessToSpace={usersWithAccessToSpace}
        onSave={(updated: Task) => {
          // ['task', id] was never a real query key — nothing in web/src read it, so this
          // was a no-op that only looked harmless while Task View read the same list cache
          // the modal writes. Task View now reads the detail record, so the AI write has to
          // land there or it reverts on the next render, and the description debounce then
          // sees no diff and never re-saves. (#3010)
          writeTaskDetailCache(updated);
        }}
      />
    </div>
  );
};
